import React, { useCallback, useEffect, useState } from "react";
import { TextField, Button, Banner, Spinner } from "@shopify/polaris";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { Badge } from "catalyst-ui-kit/badge";
import {
  WorkSecurityGroup,
  fetchWorkSecurityGroups,
  createWorkSecurityGroup,
  updateWorkSecurityGroup,
  deleteWorkSecurityGroup,
} from "services/work-security.service";
import { WorkSecurityArea } from "services/work-security-areas.service";
import AreaFormField, {
  defaultAreaSelection,
} from "features/work-security-areas/AreaFormField";
import { resolveAreaErrorMessage } from "features/work-security-areas/area-errors";

interface WorkSecurityGroupsModalProps {
  /** Accessible spheres (PR-2 §P4) — a Select is only offered in "all spheres" mode. */
  areas?: WorkSecurityArea[];
  /** The module header's concretely-selected sphere; null/undefined = "all spheres" mode. */
  selectedArea?: WorkSecurityArea | null;
  /** Called after any group create/rename/delete so the roles list can refresh. */
  onChanged?: () => void;
}

export default function WorkSecurityGroupsModal({
  areas = [],
  selectedArea,
  onChanged,
}: WorkSecurityGroupsModalProps) {
  const { t } = useTranslation();
  const { companyId } = useParams<{ companyId: string }>();

  const [groups, setGroups] = useState<WorkSecurityGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [newGroupName, setNewGroupName] = useState("");
  // Only meaningful in "all spheres" mode — see AreaFormField below. A new
  // group's sphere can't be changed later (update-group.js has no `area`),
  // so this is the only chance to get it right — hence the same "protection
  // from filing into the wrong sphere" plaque/Select as the role form.
  const [newGroupAreaId, setNewGroupAreaId] = useState(() => defaultAreaSelection(areas));
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState("");
  const [savingId, setSavingId] = useState<string | null>(null);
  const [deleteCandidateId, setDeleteCandidateId] = useState<string | null>(null);
  const showAreaField = areas.length > 1;

  const loadGroups = useCallback(async () => {
    if (!companyId) return;
    try {
      setLoading(true);
      // Scoped to the sphere in the header: renaming or deleting a group you
      // cannot even see in the roles list would be a nasty surprise.
      const data = await fetchWorkSecurityGroups(
        companyId,
        selectedArea ? { areaId: selectedArea.id } : undefined
      );
      setGroups(data.groups || []);
    } catch (err) {
      console.error("Failed to load work security groups", err);
      setError(
        t("failed_load_groups", { defaultValue: "Failed to load groups." })
      );
    } finally {
      setLoading(false);
    }
  }, [companyId, selectedArea, t]);

  useEffect(() => {
    loadGroups();
  }, [loadGroups]);

  const handleCreate = async () => {
    if (!companyId || !newGroupName.trim()) return;
    try {
      setCreating(true);
      setError("");
      await createWorkSecurityGroup(companyId, {
        name: newGroupName.trim(),
        // HARD RULE (PR-2 §P1): omit `area` entirely with 0-1 accessible
        // spheres, so this request stays byte-for-byte what it was before
        // spheres existed.
        ...(showAreaField
          ? { area: selectedArea ? selectedArea.id : newGroupAreaId }
          : {}),
      });
      setNewGroupName("");
      await loadGroups();
      if (onChanged) onChanged();
    } catch (err: any) {
      console.error("Failed to create group", err);
      setError(
        resolveAreaErrorMessage(err, t) ||
          err?.response?.data?.message ||
          t("failed_create_group", { defaultValue: "Failed to create group." })
      );
    } finally {
      setCreating(false);
    }
  };

  const handleRename = async (groupId: string) => {
    if (!companyId || !editingName.trim()) return;
    try {
      setSavingId(groupId);
      setError("");
      await updateWorkSecurityGroup(companyId, groupId, {
        name: editingName.trim(),
      });
      setEditingId(null);
      setEditingName("");
      await loadGroups();
      if (onChanged) onChanged();
    } catch (err: any) {
      console.error("Failed to rename group", err);
      setError(
        err?.response?.data?.message ||
          t("failed_update_group", { defaultValue: "Failed to rename group." })
      );
    } finally {
      setSavingId(null);
    }
  };

  const handleDelete = async (groupId: string) => {
    if (!companyId) return;
    try {
      setSavingId(groupId);
      setError("");
      await deleteWorkSecurityGroup(companyId, groupId);
      setDeleteCandidateId(null);
      await loadGroups();
      if (onChanged) onChanged();
    } catch (err: any) {
      console.error("Failed to delete group", err);
      setError(
        err?.response?.data?.message ||
          t("failed_delete_group", { defaultValue: "Failed to delete group." })
      );
    } finally {
      setSavingId(null);
    }
  };

  return (
    <div className="space-y-4">
      {error && <Banner tone="critical">{error}</Banner>}

      <div className="flex items-end gap-3">
        <div className="flex-1">
          <TextField
            label={t("group_name", { defaultValue: "Group Name" })}
            value={newGroupName}
            onChange={setNewGroupName}
            placeholder={t("group_name_placeholder", {
              defaultValue: "e.g., Warehouse",
            })}
            autoComplete="off"
            disabled={creating}
          />
        </div>
        <Button
          variant="primary"
          onClick={handleCreate}
          loading={creating}
          disabled={!newGroupName.trim()}
        >
          {t("create_group", { defaultValue: "Create Group" })}
        </Button>
      </div>

      <AreaFormField
        areas={areas}
        selectedArea={selectedArea}
        value={newGroupAreaId}
        onChange={setNewGroupAreaId}
        disabled={creating}
      />

      <p className="text-sm text-zinc-500 dark:text-zinc-400">
        {t("group_delete_hint", {
          defaultValue:
            "Deleting a group does not delete its roles — they simply become ungrouped.",
        })}
      </p>

      {loading ? (
        <div className="flex justify-center py-8">
          <Spinner size="small" />
        </div>
      ) : groups.length === 0 ? (
        <p className="text-sm text-zinc-500 dark:text-zinc-400 text-center py-6">
          {t("no_groups_yet", {
            defaultValue: "No groups yet. Create the first one above.",
          })}
        </p>
      ) : (
        <ul className="divide-y divide-zinc-200 dark:divide-zinc-800 border border-zinc-200 dark:border-zinc-800 rounded-lg">
          {groups.map((group) => (
            <li key={group.id} className="flex flex-wrap items-center gap-x-3 gap-y-2 px-4 py-3">
              {editingId === group.id ? (
                <>
                  <div className="min-w-[10rem] flex-1">
                    <TextField
                      label=""
                      labelHidden
                      value={editingName}
                      onChange={setEditingName}
                      autoComplete="off"
                      disabled={savingId === group.id}
                    />
                  </div>
                  <div className="flex gap-3">
                    <Button
                      variant="primary"
                      onClick={() => handleRename(group.id)}
                      loading={savingId === group.id}
                      disabled={!editingName.trim()}
                    >
                      {t("save", { defaultValue: "Save" })}
                    </Button>
                    <Button
                      onClick={() => {
                        setEditingId(null);
                        setEditingName("");
                      }}
                      disabled={savingId === group.id}
                    >
                      {t("cancel", { defaultValue: "Cancel" })}
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  {/* On a phone the name and its count take the first line and the two buttons wrap under them. */}
                  <span className="min-w-[10rem] flex-1 break-words text-sm font-medium text-zinc-900 dark:text-white">
                    {group.name}
                  </span>
                  <Badge color="zinc">
                    {t("roles_count", { defaultValue: "Roles" })}:{" "}
                    {group.rolesCount ?? 0}
                  </Badge>
                  <div className="flex gap-3">
                    <Button
                      onClick={() => {
                        setEditingId(group.id);
                        setEditingName(group.name);
                        setDeleteCandidateId(null);
                      }}
                      disabled={savingId !== null}
                    >
                      {t("rename", { defaultValue: "Rename" })}
                    </Button>
                    {deleteCandidateId === group.id ? (
                      <Button
                        variant="primary"
                        tone="critical"
                        onClick={() => handleDelete(group.id)}
                        loading={savingId === group.id}
                      >
                        {t("confirm_delete", { defaultValue: "Confirm delete?" })}
                      </Button>
                    ) : (
                      <Button
                        tone="critical"
                        onClick={() => setDeleteCandidateId(group.id)}
                        disabled={savingId !== null}
                      >
                        {t("delete", { defaultValue: "Delete" })}
                      </Button>
                    )}
                  </div>
                </>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
